import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BaseResponse } from "../../typings/interfaces";
import { BaseResponseSingle } from "../../typings/interfaces/responses";
import { PriceListSummary } from "../../typings/interfaces/responses/price-list-summary-response";
import { ICustomerResponse } from "../../typings/interfaces/responses/customer-response";
import {
  CLONE_PRICE_LIST,
  PRICE_LISTS,
  PRICE_LIST_BY_ID,
  SET_DEFAULT_PRICE_LIST,
  UPDATE_CUSTOMER_PRICE_LIST,
} from "../../configs/apiConfig";

// The backend answers 200 with status "FAILED" for rule violations (duplicate
// name, deleting the default…); surface those as rejections with its message.
const unwrap = <T extends { status: string; message: string }>(data: T): T => {
  if (data.status !== "SUCCESS") throw new Error(data.message || "Request failed");
  return data;
};
const errorMessage = (err: unknown) =>
  err instanceof Error ? err.message : "Request failed";

export const getPriceListsAction = createAsyncThunk(
  "priceLists/get-all",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get<BaseResponse<PriceListSummary>>(PRICE_LISTS);
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const createPriceListAction = createAsyncThunk(
  "priceLists/create",
  async (payload: { name: string; code?: string }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post<BaseResponseSingle<PriceListSummary>>(PRICE_LISTS, payload);
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const updatePriceListAction = createAsyncThunk(
  "priceLists/update",
  async (
    { id, ...payload }: { id: number; name?: string; code?: string; active?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.put<BaseResponseSingle<PriceListSummary>>(PRICE_LIST_BY_ID(id), payload);
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const clonePriceListAction = createAsyncThunk(
  "priceLists/clone",
  async (
    { id, fromId, overwrite }: { id: number; fromId: number; overwrite: boolean },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.post<BaseResponseSingle<number>>(CLONE_PRICE_LIST(id, fromId, overwrite));
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const setDefaultPriceListAction = createAsyncThunk(
  "priceLists/set-default",
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await axios.post<BaseResponseSingle<PriceListSummary>>(SET_DEFAULT_PRICE_LIST(id));
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

export const deletePriceListAction = createAsyncThunk(
  "priceLists/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete<BaseResponseSingle<null>>(PRICE_LIST_BY_ID(id));
      unwrap(data);
      return id;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);

/** Assigns a customer's price list; null returns them to the default list. */
export const updateCustomerPriceListAction = createAsyncThunk(
  "priceLists/assign-customer",
  async (
    { customerId, priceListId }: { customerId: number | string; priceListId: number | null },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axios.put<BaseResponseSingle<ICustomerResponse>>(
        UPDATE_CUSTOMER_PRICE_LIST(customerId),
        { priceListId }
      );
      return unwrap(data).body;
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  }
);
